import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { AccessibleLoader } from '@/components/shared/AccessibleLoader';
import { SubscriptionLimitBanner } from '@/components/shared/SubscriptionLimitBanner';
import { CategoryScoreBar } from '@/components/site-audit/CategoryScoreBar';
import { IssueCard } from '@/components/site-audit/IssueCard';
import { useAuth } from '@/utils/AuthContext';
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate';
import {
  builderApi,
  siteAuditApi,
  AUDIT_CATEGORIES,
  AUDIT_SEVERITY_ORDER,
  type AuditIssue,
  type SiteAudit,
} from '@/utils/api-pb';

/** Free видит главную проблему целиком, остальные — только заголовком. */
const FREE_VISIBLE_ISSUES = 1;

function scoreTone(score: number): string {
  if (score >= 80) return 'text-[#2e7d32]';
  if (score >= 50) return 'text-[#a35b00]';
  return 'text-[color:var(--error-strong,#b3261e)]';
}

/**
 * One audit, in the order the owner should act on it.
 *
 * Sorted by severity rather than grouped by category: the question this page answers is
 * "what do I fix first", and a critical runtime error and a critical CTA problem are
 * the same answer. Category scores sit above as the map.
 */
export default function SiteAuditReportPage() {
  const { auditId = '' } = useParams();
  const { t, i18n } = useTranslation('siteAudit');
  const { isFree } = useAuth();
  const navigate = useLocalizedNavigate();

  const [audit, setAudit] = useState<SiteAudit | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRerunning, setIsRerunning] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoading(true);
      try {
        const record = await siteAuditApi.getOne(auditId);
        if (!cancelled) setAudit(record);
      } catch (error) {
        if (!cancelled) {
          toast.error(error instanceof Error ? error.message : t('toast.error'));
          navigate('/site-audit');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [auditId, navigate, t]);

  const issues = useMemo(() => {
    const list = audit?.report?.issues ?? [];
    return [...list].sort(
      (a, b) => AUDIT_SEVERITY_ORDER.indexOf(a.severity) - AUDIT_SEVERITY_ORDER.indexOf(b.severity),
    );
  }, [audit]);

  const issuesByCategory = useMemo(() => {
    const counts = {} as Record<string, number>;
    for (const issue of issues) counts[issue.category] = (counts[issue.category] ?? 0) + 1;
    return counts;
  }, [issues]);

  async function handleRerun() {
    if (!audit) return;
    setIsRerunning(true);
    try {
      const next = await siteAuditApi.start({
        url: audit.build ? undefined : audit.url,
        buildId: audit.build || undefined,
        projectId: audit.project || undefined,
        lang: i18n.language as 'ru' | 'en',
      });
      toast.success(t('toast.started'));
      navigate('/site-audit');
      void next;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('toast.error'));
    } finally {
      setIsRerunning(false);
    }
  }

  /**
   * Sends the fix into the builder's edit chat.
   *
   * Only offered for sites built here — the report carries `builderAction` exactly for
   * those — so the owner goes from "here is the problem" to a corrected page without
   * retyping anything.
   */
  async function handleFixInBuilder(issue: AuditIssue) {
    if (!audit?.build || !issue.builderAction) return;
    try {
      await builderApi.chat(audit.build, issue.builderAction.message, i18n.language as 'ru' | 'en');
      toast.success(t('report.sentToBuilder'));
      navigate(`/builder/${audit.build}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('toast.error'));
    }
  }

  if (isLoading) return <AccessibleLoader label={t('loading', { ns: 'common' })} />;
  if (!audit) return null;

  if (audit.status !== 'completed') {
    return (
      <div className="flex flex-col gap-[16px]">
        <Button variant="brand-outline" size="brand-sm" className="self-start" onClick={() => navigate('/site-audit')}>
          {t('report.back')}
        </Button>
        <p className="rounded-[16px] bg-[var(--grey-background,#f8f8f8)] px-[24px] py-[32px] text-center font-segoe text-[15px] text-[color:var(--grey-text,#565858)]">
          {audit.status === 'failed' ? audit.error || t('toast.failed') : t('status.pending')}
        </p>
      </div>
    );
  }

  const score = audit.score ?? 0;

  return (
    <div className="flex flex-col gap-[24px]">
      <div className="flex flex-wrap items-center gap-[12px]">
        <Button variant="brand-outline" size="brand-sm" onClick={() => navigate('/site-audit')}>
          {t('report.back')}
        </Button>
        <a
          href={audit.final_url || audit.url}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="truncate font-segoe text-[14px] text-[color:var(--main-brand,#2197f1)] hover:underline"
        >
          {(audit.final_url || audit.url).replace(/^https?:\/\//, '')}
        </a>
        <Button
          variant="brand"
          size="brand-sm"
          className="ml-auto"
          onClick={handleRerun}
          disabled={isRerunning}
        >
          {t('report.rerun')}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-[24px] rounded-[16px] border border-[var(--grey-accent,#eeeeee)] px-[24px] py-[20px]">
        <div className="flex items-baseline gap-[8px]">
          <span className={cn('font-manrope text-[44px] font-bold leading-none', scoreTone(score))}>
            {score}
          </span>
          <span className="font-segoe text-[15px] text-[color:var(--grey-text,#565858)]">
            {t('report.outOf')}
          </span>
        </div>
        <div className="flex flex-col gap-[2px]">
          <span className="font-manrope text-[15px] font-semibold text-[color:var(--black-text,#0d0d0d)]">
            {t('report.issuesFound', { count: audit.issues_count ?? issues.length })}
          </span>
          <span className="font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">
            {new Date(audit.created).toLocaleString(i18n.language === 'en' ? 'en-US' : 'ru-RU')}
          </span>
        </div>
      </div>

      {/* Оффер без проекта не проверялся — и об этом сказано прямо, а не «сто из ста». */}
      {audit.report?.meta && !audit.report.meta.offerAnalyzed && (
        <p className="rounded-[16px] bg-[var(--grey-background,#f8f8f8)] px-[16px] py-[12px] font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">
          {t('report.offerSkipped')}
        </p>
      )}

      <div className="grid gap-[16px] sm:grid-cols-2 xl:grid-cols-3">
        {AUDIT_CATEGORIES.map((category) => (
          <CategoryScoreBar
            key={category}
            category={category}
            score={audit.category_scores?.[category] ?? null}
            issues={issuesByCategory[category] ?? 0}
          />
        ))}
      </div>

      {isFree && issues.length > FREE_VISIBLE_ISSUES && (
        <SubscriptionLimitBanner description={t('report.freeLimit')} />
      )}

      <ul className="flex flex-col gap-[8px]">
        {issues.map((issue, index) => (
          <IssueCard
            key={`${issue.id}-${index}`}
            issue={issue}
            locked={isFree && index >= FREE_VISIBLE_ISSUES}
            onUpgrade={() => navigate('/subscription')}
            onFixInBuilder={audit.build ? handleFixInBuilder : undefined}
          />
        ))}
      </ul>
    </div>
  );
}
